import { sanitizeSku } from "@shared/sku";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Ban, Check, RefreshCw, ShoppingBag, Store, X } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiClientError, api } from "../lib/api";
import { integer, money, whenever } from "../lib/format";
import i18n from "../lib/i18n";
import { defaultLicenseKindForKind } from "../lib/licenseDefaults";
import { cn } from "../lib/utils";
import type { LicenseKind } from "./Inventory";

type Unit = "stickers" | "figures" | "electronics";
type ProductKind =
  | "sticker_single"
  | "sticker_sheet"
  | "card_wrap"
  | "figure"
  | "electronics"
  | "bundle"
  | "other";

const INVENTORY_UNITS: Unit[] = ["stickers", "figures", "electronics"];
const PRODUCT_KINDS: ProductKind[] = [
  "sticker_single",
  "sticker_sheet",
  "card_wrap",
  "figure",
  "electronics",
  "bundle",
  "other",
];
const LICENSE_KINDS: LicenseKind[] = [
  "original",
  "licensed",
  "public_domain",
  "generic",
  "unreviewed",
];

type Marketplace = "shopify" | "amazon" | "walmart" | "ebay" | "tiktok_shop" | "wholesale";
type ListingStatus = "draft" | "pending" | "active" | "suppressed" | "ended";

interface ChannelRow {
  id: string;
  marketplace: Marketplace;
  accountLabel: string;
  url: string | null;
  feePercent: string | null;
  currency: string;
  isActive: boolean;
  listingCount: number;
  activeCount: number;
  suppressedCount: number;
  pendingUnmatchedCount: number;
}

interface ListingRow {
  id: string;
  sku: string;
  title: string;
  businessUnit: string;
  licenseKind: string;
  marketplace: Marketplace;
  accountLabel: string;
  status: ListingStatus;
  price: string | null;
  externalId: string | null;
  /** Seller SKU on that platform — see ARCHITECTURE.md §1. */
  channelSku: string | null;
  /** The marketplace's own id (ASIN / ItemID / wpid / variant id). */
  marketplaceId: string | null;
  publishedQuantity: number | null;
  suppressionReason: string | null;
  lastSyncedAt: string | null;
  sellable: number;
}

interface DriftRow {
  sku: string;
  title: string;
  marketplace: Marketplace;
  accountLabel: string;
  status: ListingStatus;
  publishedQuantity: number | null;
  sellable: number;
  drift: number;
}

const STATUS_VARIANT: Record<
  ListingStatus,
  "secondary" | "info" | "success" | "destructive" | "outline"
> = {
  draft: "secondary",
  pending: "info",
  active: "success",
  suppressed: "destructive",
  ended: "outline",
};

interface SyncSummary {
  marketplace: string;
  fetched: number;
  matched: number;
  unmatchedSkus: string[];
  ended: number;
  autoCreated: number;
  autoLinked: number;
}

interface UnmatchedSkuRow {
  id: string;
  channelId: string;
  marketplace: Marketplace;
  accountLabel: string;
  channelSku: string;
  title: string | null;
  category: string | null;
  externalId: string | null;
  price: string | null;
  publishedQuantity: number | null;
  remoteStatus: ListingStatus | null;
  resolution: "pending" | "mapped" | "created" | "ignored";
  firstSeenAt: string;
  lastSeenAt: string;
}

interface UnmatchedSkuDetail {
  title: string | null;
  description: string | null;
  price: string | null;
  quantity: number | null;
  status: ListingStatus | null;
  category: string | null;
  url: string | null;
}

interface ProductSearchHit {
  id: string;
  sku: string;
  title: string;
}

export default function Marketplaces() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();
  /* Deep-link support: the Inventory page links here with
   * ?marketplace=<channel>&q=<marketplace sku>, so clicking a marketplace SKU
   * in its per-channel breakdown lands on this matrix already narrowed to
   * that one listing instead of dumping the operator into an unfiltered
   * table they then have to search by hand. Read once on mount -- this is a
   * starting point for the filters, not a controlled binding. */
  const initialParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const initialMarketplace = initialParams.get("marketplace");
  const [filter, setFilter] = useState<Marketplace | "all">(
    (initialMarketplace as Marketplace | null) ?? "all",
  );
  const [matrixSearch, setMatrixSearch] = useState(initialParams.get("q") ?? "");
  const [syncResults, setSyncResults] = useState<Record<string, SyncSummary>>({});
  const [syncErrors, setSyncErrors] = useState<Record<string, string>>({});

  const channelsQ = useQuery({
    queryKey: ["channels"],
    queryFn: () => api<ChannelRow[]>("/api/v1/channels"),
  });

  const syncMutation = useMutation({
    mutationFn: (channelId: string) =>
      api<SyncSummary>(`/api/v1/channels/${channelId}/sync`, { method: "POST" }),
    onSuccess: (summary, channelId) => {
      setSyncResults((prev) => ({ ...prev, [channelId]: summary }));
      setSyncErrors((prev) => {
        const next = { ...prev };
        delete next[channelId];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ["channels"] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
      void qc.invalidateQueries({ queryKey: ["listing-drift"] });
    },
    onError: (err, channelId) => {
      setSyncErrors((prev) => ({
        ...prev,
        [channelId]: err instanceof ApiClientError ? err.message : t("marketplaces:syncFailed"),
      }));
    },
  });
  const listingsQ = useQuery({
    queryKey: ["listings", filter],
    queryFn: () =>
      api<ListingRow[]>(
        `/api/v1/listings${filter === "all" ? "" : `?marketplace=${encodeURIComponent(filter)}`}`,
      ),
  });
  const driftQ = useQuery({
    queryKey: ["listing-drift"],
    queryFn: () => api<DriftRow[]>("/api/v1/listings/drift"),
  });

  /* ─────────────────────────── unmatched SKUs ─────────────────────────── */

  const [unmatchedChannelFilter, setUnmatchedChannelFilter] = useState<string | "all">("all");
  // Isaac, 2026-10-01: "I only want to see listings that are active on my
  // account so we can make adjustments based on real time data" -- defaults
  // to active-only now that remote_status is actually kept current (see the
  // ended-reconciliation fix in sync.ts); "all" shows Ended/Draft too, for
  // reviewing past/stale rows on purpose.
  const [unmatchedStatusFilter, setUnmatchedStatusFilter] = useState<"active" | "all">("active");
  const [unmatchedLimit, setUnmatchedLimit] = useState(50);
  const [selectedUnmatchedIds, setSelectedUnmatchedIds] = useState<Set<string>>(new Set());
  const [actionRowId, setActionRowId] = useState<string | null>(null);
  const [actionMode, setActionMode] = useState<"map" | "create" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [detailRowId, setDetailRowId] = useState<string | null>(null);

  const detailQ = useQuery({
    queryKey: ["unmatched-sku-detail", detailRowId],
    queryFn: () => api<UnmatchedSkuDetail>(`/api/v1/unmatched-skus/${detailRowId}/detail`),
    enabled: detailRowId !== null,
  });

  const [mapSearch, setMapSearch] = useState("");
  const [mapResults, setMapResults] = useState<ProductSearchHit[]>([]);
  const [mapSearching, setMapSearching] = useState(false);

  const [createSku, setCreateSku] = useState("");
  const [createTitle, setCreateTitle] = useState("");
  const [createUnit, setCreateUnit] = useState<Unit>("stickers");
  const [createKind, setCreateKind] = useState<ProductKind>("sticker_single");
  const [createLicense, setCreateLicense] = useState<LicenseKind>("unreviewed");
  const [createFranchise, setCreateFranchise] = useState("");

  /* ───────────────────── bulk create (unmatched → products) ───────────────────── */
  // The one-at-a-time Create SKU flow above doesn't scale to a 600+ row
  // backlog any better than one-at-a-time ignoring did (see bulkIgnoreMutation) —
  // this wires up the bulk-create-products endpoint that already existed
  // server-side (built 2026-08-29 for exactly this gap) but was never exposed
  // in this page.
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [bulkCreateUnit, setBulkCreateUnit] = useState<Unit>("stickers");
  const [bulkCreateKind, setBulkCreateKind] = useState<ProductKind>("other");
  const [bulkCreateLicense, setBulkCreateLicense] = useState<LicenseKind>("unreviewed");
  interface BulkCreateResult {
    created: number;
    linkedToExisting: number;
    siblingsResolved: number;
    skipped: Array<{ channelSku: string; reason: string }>;
  }
  const [bulkCreateResult, setBulkCreateResult] = useState<BulkCreateResult | null>(null);

  const unmatchedParams = new URLSearchParams({
    resolution: "pending",
    limit: String(unmatchedLimit),
  });
  if (unmatchedChannelFilter !== "all") unmatchedParams.set("channelId", unmatchedChannelFilter);
  if (unmatchedStatusFilter === "active") unmatchedParams.set("remoteStatus", "active");

  const unmatchedQ = useQuery({
    queryKey: ["unmatched-skus", unmatchedChannelFilter, unmatchedStatusFilter, unmatchedLimit],
    queryFn: () => api<UnmatchedSkuRow[]>(`/api/v1/unmatched-skus?${unmatchedParams.toString()}`),
  });
  const unmatchedSummaryQ = useQuery({
    queryKey: ["unmatched-skus-summary"],
    queryFn: () =>
      api<
        Array<{
          channelId: string;
          marketplace: Marketplace;
          accountLabel: string;
          activeCount: number;
          pendingCount: number;
        }>
      >("/api/v1/unmatched-skus/summary"),
  });

  function closeAction() {
    setActionRowId(null);
    setActionMode(null);
    setActionError(null);
    setMapSearch("");
    setMapResults([]);
    setCreateSku("");
    setCreateTitle("");
    setCreateFranchise("");
    setCreateUnit("stickers");
    setCreateKind("sticker_single");
    setCreateLicense("unreviewed");
  }

  function openMap(row: UnmatchedSkuRow) {
    setActionRowId(row.id);
    setActionMode("map");
    setActionError(null);
    setMapSearch(row.channelSku);
    setMapResults([]);
  }

  function openCreate(row: UnmatchedSkuRow) {
    setActionRowId(row.id);
    setActionMode("create");
    setActionError(null);
    setCreateSku(sanitizeSku(row.channelSku));
    setCreateTitle(row.title ?? "");
    // eBay's unmatched backlog is overwhelmingly one-off parts/finds (a
    // model number or a short description as the seller's own "SKU"), not
    // catalog stickers -- Isaac, 2026-09-19. Amazon/Walmart/Shopify keep the
    // sticker_single/stickers starting point; that's where the sticker
    // catalog actually lives.
    const kind: ProductKind = row.marketplace === "ebay" ? "other" : "sticker_single";
    const unit: Unit = row.marketplace === "ebay" ? "electronics" : "stickers";
    setCreateKind(kind);
    setCreateUnit(unit);
    setCreateLicense(defaultLicenseKindForKind(kind));
  }

  const invalidateUnmatched = () => {
    void qc.invalidateQueries({ queryKey: ["unmatched-skus"] });
    void qc.invalidateQueries({ queryKey: ["unmatched-skus-summary"] });
    void qc.invalidateQueries({ queryKey: ["channels"] });
    void qc.invalidateQueries({ queryKey: ["listings"] });
    void qc.invalidateQueries({ queryKey: ["listing-drift"] });
  };

  const mapMutation = useMutation({
    mutationFn: ({ id, productId }: { id: string; productId: string }) =>
      api(`/api/v1/unmatched-skus/${id}/map`, {
        method: "POST",
        body: JSON.stringify({ productId }),
      }),
    onSuccess: () => {
      invalidateUnmatched();
      closeAction();
    },
    onError: (err) =>
      setActionError(err instanceof ApiClientError ? err.message : t("marketplaces:actionFailed")),
  });

  const createMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/api/v1/unmatched-skus/${id}/create-product`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateUnmatched();
      closeAction();
    },
    onError: (err) =>
      setActionError(err instanceof ApiClientError ? err.message : t("marketplaces:actionFailed")),
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: string) => api(`/api/v1/unmatched-skus/${id}/ignore`, { method: "POST" }),
    onSuccess: () => invalidateUnmatched(),
  });

  function toggleUnmatchedSelection(id: string) {
    setSelectedUnmatchedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const bulkIgnoreMutation = useMutation({
    mutationFn: (ids: string[]) =>
      api<{ ignored: number }>("/api/v1/unmatched-skus/bulk-ignore", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      setSelectedUnmatchedIds(new Set());
      invalidateUnmatched();
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: (ids: string[]) =>
      api<BulkCreateResult>("/api/v1/unmatched-skus/bulk-create-products", {
        method: "POST",
        body: JSON.stringify({
          ids,
          businessUnit: bulkCreateUnit,
          kind: bulkCreateKind,
          licenseKind: bulkCreateLicense,
        }),
      }),
    onSuccess: (result) => {
      setBulkCreateResult(result);
      setSelectedUnmatchedIds(new Set());
      invalidateUnmatched();
    },
  });

  function closeBulkCreate() {
    setBulkCreateOpen(false);
    setBulkCreateResult(null);
    setBulkCreateUnit("stickers");
    setBulkCreateKind("other");
    setBulkCreateLicense("unreviewed");
  }

  async function runProductSearch() {
    const q = mapSearch.trim();
    if (!q) {
      setMapResults([]);
      return;
    }
    setMapSearching(true);
    try {
      const hits = await api<ProductSearchHit[]>(
        `/api/v1/product-search?q=${encodeURIComponent(q)}`,
      );
      setMapResults(hits);
    } catch {
      setMapResults([]);
    } finally {
      setMapSearching(false);
    }
  }

  function handleCreateSubmit(e: React.FormEvent, row: UnmatchedSkuRow) {
    e.preventDefault();
    setActionError(null);
    createMutation.mutate({
      id: row.id,
      body: {
        sku: sanitizeSku(createSku) || undefined,
        title: createTitle.trim(),
        businessUnit: createUnit,
        kind: createKind,
        licenseKind: createLicense,
        franchise: createFranchise.trim() || undefined,
      },
    });
  }

  const channels = channelsQ.data ?? [];
  const allListings = listingsQ.data ?? [];
  const matrixNeedle = matrixSearch.trim().toLowerCase();
  const listings = matrixNeedle
    ? allListings.filter((l) =>
        [l.sku, l.title, l.channelSku, l.externalId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(matrixNeedle)),
      )
    : allListings;
  /** Derived from actual connected channels, not hardcoded — a filter list that
   * has to be remembered on every new channel is exactly how eBay went missing
   * from here after being wired up everywhere else. */
  const availableMarketplaces = Array.from(new Set(channels.map((c) => c.marketplace)));
  /** Positive drift is the dangerous direction: more published than we can ship. */
  const oversellRisk = (driftQ.data ?? []).filter((d) => d.drift > 0);

  return (
    <>
      <div className="mb-6">
        <h1>{t("navigation:marketplaces")}</h1>
        <p className="mt-2 text-muted-foreground">{t("marketplaces:subtitle")}</p>
      </div>

      <section className="mb-8">
        <h2 className="mb-3">{t("marketplaces:channels")}</h2>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {channels.map((c) => (
            <li key={c.id}>
              <Card className="h-full p-4">
                <span className="flex items-center gap-2 font-medium">
                  {c.marketplace === "shopify" ? (
                    <Store className="h-4 w-4" aria-hidden />
                  ) : (
                    <ShoppingBag className="h-4 w-4" aria-hidden />
                  )}
                  {t(`marketplaces:name.${c.marketplace}`)}
                  <span className="ml-auto text-xs text-muted-foreground">{c.accountLabel}</span>
                </span>
                <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <Cell
                    label={t("marketplaces:listings")}
                    value={integer(c.listingCount, locale)}
                  />
                  <Cell label={t("marketplaces:active")} value={integer(c.activeCount, locale)} />
                  <Cell
                    label={t("marketplaces:suppressed")}
                    value={integer(c.suppressedCount, locale)}
                    bad={c.suppressedCount > 0}
                  />
                </dl>
                {c.pendingUnmatchedCount > 0 && (
                  // The listing counts above only ever count MATCHED
                  // listings -- a channel can have real data (Amazon's own
                  // bulk sync in particular can only discover skus via this
                  // table, not the listing counts above) while still showing
                  // 0/0/0 up there. Surfaced here so that doesn't read as
                  // "not connected."
                  <button
                    type="button"
                    className="mt-2 block w-full rounded-md border border-dashed border-amber-500/50 bg-amber-500/10 p-2 text-left text-xs text-amber-700 hover:bg-amber-500/20 dark:text-amber-400"
                    onClick={() => {
                      setUnmatchedChannelFilter(c.id);
                      setSelectedUnmatchedIds(new Set());
                      document
                        .getElementById("unmatched-skus")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    {t("marketplaces:pendingUnmatchedCta", {
                      count: c.pendingUnmatchedCount,
                    })}
                  </button>
                )}

                <p className="mt-3 text-xs text-muted-foreground">
                  {t("marketplaces:feeLabel")}: {c.feePercent ? `${c.feePercent}%` : "—"}
                </p>

                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full"
                  disabled={syncMutation.isPending && syncMutation.variables === c.id}
                  onClick={() => syncMutation.mutate(c.id)}
                >
                  <RefreshCw
                    className={cn(
                      "h-3.5 w-3.5",
                      syncMutation.isPending && syncMutation.variables === c.id && "animate-spin",
                    )}
                    aria-hidden
                  />
                  {t("marketplaces:syncNow")}
                </Button>

                {syncErrors[c.id] && (
                  <p className="mt-2 text-xs text-destructive">{syncErrors[c.id]}</p>
                )}
                {syncResults[c.id] && !syncErrors[c.id] && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t("marketplaces:syncResult", {
                      fetched: syncResults[c.id]?.fetched ?? 0,
                      matched: syncResults[c.id]?.matched ?? 0,
                    })}
                    {/* unmatchedSkus is the raw count found mid-sync, BEFORE
                        autoCreateMatchingProducts resolves the already-
                        Ninjamo-formatted ones a moment later — subtract those
                        out so this reads as "still needs a human", not a
                        stale pre-resolution number. */}
                    {(() => {
                      const result = syncResults[c.id];
                      if (!result) return null;
                      const stillUnmatched = Math.max(
                        0,
                        result.unmatchedSkus.length - result.autoCreated - result.autoLinked,
                      );
                      return (
                        <>
                          {(result.autoCreated > 0 || result.autoLinked > 0) &&
                            ` · ${t("marketplaces:syncAutoResolved", {
                              count: result.autoCreated + result.autoLinked,
                            })}`}
                          {stillUnmatched > 0 &&
                            ` · ${t("marketplaces:syncUnmatched", { count: stillUnmatched })}`}
                          {result.ended > 0 &&
                            ` · ${t("marketplaces:syncEnded", { count: result.ended })}`}
                        </>
                      );
                    })()}
                  </p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      </section>

      <section id="unmatched-skus" className="mb-8">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <h2>{t("marketplaces:unmatchedSkus")}</h2>
            {unmatchedSummaryQ.data && unmatchedSummaryQ.data.length > 0 && (
              <Badge variant="warning">
                {integer(
                  unmatchedSummaryQ.data.reduce(
                    (sum, r) =>
                      sum + (unmatchedStatusFilter === "active" ? r.activeCount : r.pendingCount),
                    0,
                  ),
                  locale,
                )}
              </Badge>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              value={unmatchedStatusFilter}
              onValueChange={(v) => {
                setUnmatchedStatusFilter(v as "active" | "all");
                setSelectedUnmatchedIds(new Set());
              }}
            >
              <SelectTrigger className="w-full sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t("marketplaces:activeOnly")}</SelectItem>
                <SelectItem value="all">{t("marketplaces:allStatuses")}</SelectItem>
              </SelectContent>
            </Select>
            {(unmatchedSummaryQ.data?.length ?? 0) > 1 && (
              <Select
                value={unmatchedChannelFilter}
                onValueChange={(v) => {
                  setUnmatchedChannelFilter(v);
                  setSelectedUnmatchedIds(new Set());
                }}
              >
                <SelectTrigger className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("marketplaces:allChannels")}</SelectItem>
                  {unmatchedSummaryQ.data?.map((s) => (
                    <SelectItem key={s.channelId} value={s.channelId}>
                      {t(`marketplaces:name.${s.marketplace}`)} · {s.accountLabel} (
                      {integer(
                        unmatchedStatusFilter === "active" ? s.activeCount : s.pendingCount,
                        locale,
                      )}
                      )
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          {t("marketplaces:unmatchedSkusExplainer")}
        </p>

        {!unmatchedQ.isLoading && (unmatchedQ.data?.length ?? 0) === 0 && (
          <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            {/* "Nothing active" and "nothing at all" are different claims —
                saying the wrong one is exactly the kind of mismatch that
                started this fix (Isaac, 2026-10-01). */}
            {unmatchedStatusFilter === "active" &&
            (unmatchedSummaryQ.data
              ?.filter(
                (s) => unmatchedChannelFilter === "all" || s.channelId === unmatchedChannelFilter,
              )
              .some((s) => s.pendingCount > s.activeCount) ??
              false)
              ? t("marketplaces:noActiveUnmatchedSkus")
              : t("marketplaces:noUnmatchedSkus")}
          </p>
        )}

        {/* Bulk action bar — same shape as Inventory's unmatched-listing
            bulk-create bar: a count, Clear, and the one action that matters.
            Ignoring one at a time was never going to close a 700-row
            backlog any better than creating SKUs one at a time did. */}
        {selectedUnmatchedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border bg-card p-3">
            <span className="text-sm font-medium">
              {t("marketplaces:unmatchedSelectedCount", { count: selectedUnmatchedIds.size })}
            </span>
            <span className="ml-auto inline-flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setSelectedUnmatchedIds(new Set())}
              >
                {t("marketplaces:clearSelection")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setBulkCreateOpen(true)}
              >
                {t("marketplaces:bulkCreateButton", { count: selectedUnmatchedIds.size })}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={bulkIgnoreMutation.isPending}
                onClick={() => bulkIgnoreMutation.mutate([...selectedUnmatchedIds])}
              >
                {t("marketplaces:ignoreSelected", { count: selectedUnmatchedIds.size })}
              </Button>
            </span>
          </div>
        )}

        {(unmatchedQ.data?.length ?? 0) > 0 && selectedUnmatchedIds.size === 0 && (
          <div className="mb-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setSelectedUnmatchedIds(new Set(unmatchedQ.data?.map((r) => r.id) ?? []))
              }
            >
              {t("marketplaces:selectAllOnPage", { count: unmatchedQ.data?.length ?? 0 })}
            </Button>
          </div>
        )}

        {(unmatchedQ.data?.length ?? 0) > 0 && (
          <Card>
            {/* table-fixed is deliberate, not cosmetic: the default `auto` table
                layout sizes each column to fit its widest content, which means a
                long item title just grows the column — `max-width`/`truncate` on
                a cell do nothing under `auto` layout because the browser never
                gives the cell a bounded width to truncate against in the first
                place. Real bug this shipped with: titles were overlapping the
                Channel/Price columns on desktop. `table-fixed` + explicit
                per-column widths on the header row give truncate something to
                actually clip against.
                No forced min-width below `sm:` — with 6 columns hidden down to 2
                on a phone, the row-action buttons (the entire point of this
                screen) would otherwise sit off-screen behind an undiscoverable
                horizontal scroll. Channel/price/qty/last-synced reappear from
                `sm:` up. */}
            <Table className="table-fixed sm:min-w-[48rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8 p-0 text-center">
                    <Checkbox
                      checked={
                        (unmatchedQ.data?.length ?? 0) > 0 &&
                        (unmatchedQ.data?.every((r) => selectedUnmatchedIds.has(r.id)) ?? false)
                      }
                      onCheckedChange={(checked) =>
                        setSelectedUnmatchedIds(
                          checked ? new Set(unmatchedQ.data?.map((r) => r.id) ?? []) : new Set(),
                        )
                      }
                      aria-label={t("marketplaces:selectAllOnPage", {
                        count: unmatchedQ.data?.length ?? 0,
                      })}
                    />
                  </TableHead>
                  <TableHead className="w-[70%] sm:w-[22rem]">{t("marketplaces:item")}</TableHead>
                  <TableHead className="hidden sm:table-cell sm:w-24">
                    {t("marketplaces:channel")}
                  </TableHead>
                  <TableHead className="hidden text-right sm:table-cell sm:w-20">
                    {t("marketplaces:price")}
                  </TableHead>
                  <TableHead className="hidden text-right sm:table-cell sm:w-20">
                    {t("marketplaces:published")}
                  </TableHead>
                  <TableHead className="hidden sm:table-cell sm:w-32">
                    {t("marketplaces:lastSynced")}
                  </TableHead>
                  <TableHead className="w-[30%] text-right sm:w-56">
                    {t("marketplaces:actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmatchedQ.data?.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      <TableCell className="w-8 p-0 text-center">
                        <Checkbox
                          checked={selectedUnmatchedIds.has(row.id)}
                          onCheckedChange={() => toggleUnmatchedSelection(row.id)}
                          aria-label={t("marketplaces:selectListing", {
                            sku: row.title ?? row.channelSku,
                          })}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          // w-full is load-bearing, not decorative: a <button> is a
                          // form control, not a plain block box — Chrome sizes it to
                          // its own content by default even with `display:block`, so
                          // without an explicit width it happily renders wider than
                          // its <td> and the title text overlaps whatever's next to
                          // it. This is the actual desktop overlap bug reported.
                          className="block w-full text-left underline-offset-2 hover:underline"
                          onClick={() => setDetailRowId(row.id)}
                        >
                          <span className="block truncate font-medium">
                            {row.title ?? row.channelSku}
                          </span>
                          {/* Most unmatched rows are genuinely still live, so this
                              only fires for the exceptional/actionable case — "no
                              longer found on the channel" — rather than badging
                              every row with an "Active"/"Unknown" label it doesn't
                              need. */}
                          {row.remoteStatus === "ended" && (
                            <Badge variant={STATUS_VARIANT.ended} className="mt-0.5">
                              {t("marketplaces:listingStatus.ended")}
                            </Badge>
                          )}
                          {row.title && (
                            <span className="block truncate font-mono text-xs text-muted-foreground">
                              {row.channelSku}
                            </span>
                          )}
                          {row.category && (
                            <span className="hidden truncate text-xs text-muted-foreground sm:block">
                              {row.category}
                            </span>
                          )}
                        </button>
                        {/* Channel collapses into this cell below `sm:` instead of
                            vanishing outright — which marketplace this is still
                            matters even when there's no room for its own column. */}
                        <span className="block text-xs text-muted-foreground sm:hidden">
                          {t(`marketplaces:name.${row.marketplace}`)} · {row.accountLabel}
                        </span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {t(`marketplaces:name.${row.marketplace}`)}
                        <span className="block text-xs text-muted-foreground">
                          {row.accountLabel}
                        </span>
                      </TableCell>
                      <TableCell className="hidden text-right tabular-nums sm:table-cell">
                        {money(row.price, "USD", locale)}
                      </TableCell>
                      <TableCell className="hidden text-right tabular-nums sm:table-cell">
                        {integer(row.publishedQuantity, locale)}
                      </TableCell>
                      <TableCell className="hidden text-xs text-muted-foreground sm:table-cell">
                        {whenever(row.lastSeenAt, locale)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              actionRowId === row.id && actionMode === "map" ? "default" : "outline"
                            }
                            onClick={() =>
                              actionRowId === row.id && actionMode === "map"
                                ? closeAction()
                                : openMap(row)
                            }
                          >
                            {t("marketplaces:mapToSku")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              actionRowId === row.id && actionMode === "create"
                                ? "default"
                                : "outline"
                            }
                            onClick={() =>
                              actionRowId === row.id && actionMode === "create"
                                ? closeAction()
                                : openCreate(row)
                            }
                          >
                            {t("marketplaces:createSku")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            title={t("marketplaces:ignoreSku")}
                            disabled={
                              ignoreMutation.isPending && ignoreMutation.variables === row.id
                            }
                            onClick={() => ignoreMutation.mutate(row.id)}
                          >
                            <X className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {actionRowId === row.id && actionMode === "map" && (
                      <TableRow key={`${row.id}-map`}>
                        <TableCell colSpan={7} className="bg-muted/30">
                          <div className="flex flex-col gap-2 py-1 sm:flex-row sm:items-end">
                            <div className="flex-1">
                              <Label htmlFor="map-search">{t("marketplaces:searchSku")}</Label>
                              <Input
                                id="map-search"
                                value={mapSearch}
                                onChange={(e) => setMapSearch(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void runProductSearch();
                                  }
                                }}
                                className="mt-1"
                              />
                            </div>
                            <Button type="button" size="sm" onClick={() => void runProductSearch()}>
                              {mapSearching ? t("common:loading") : t("marketplaces:search")}
                            </Button>
                          </div>
                          {mapResults.length > 0 && (
                            <ul className="mt-2 flex flex-wrap gap-2">
                              {mapResults.map((p) => (
                                <li key={p.id}>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    disabled={mapMutation.isPending}
                                    onClick={() =>
                                      mapMutation.mutate({ id: row.id, productId: p.id })
                                    }
                                  >
                                    <Check className="h-3.5 w-3.5" aria-hidden />
                                    <span className="font-mono">{p.sku}</span>
                                    <span className="text-muted-foreground">{p.title}</span>
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {actionError && (
                            <p className="mt-2 text-xs text-destructive">{actionError}</p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}

                    {actionRowId === row.id && actionMode === "create" && (
                      <TableRow key={`${row.id}-create`}>
                        <TableCell colSpan={7} className="bg-muted/30">
                          <form
                            onSubmit={(e) => handleCreateSubmit(e, row)}
                            className="grid grid-cols-1 gap-3 py-1 sm:grid-cols-2 lg:grid-cols-4"
                          >
                            <div>
                              <Label htmlFor="create-sku">{t("inventory:sku")}</Label>
                              <Input
                                id="create-sku"
                                value={createSku}
                                onChange={(e) => setCreateSku(e.target.value.toUpperCase())}
                                required
                                className="mt-1"
                              />
                            </div>
                            <div className="lg:col-span-2">
                              <Label htmlFor="create-title">{t("inventory:product")}</Label>
                              <Input
                                id="create-title"
                                value={createTitle}
                                onChange={(e) => setCreateTitle(e.target.value)}
                                required
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="create-franchise">{t("inventory:franchise")}</Label>
                              <Input
                                id="create-franchise"
                                value={createFranchise}
                                onChange={(e) => setCreateFranchise(e.target.value)}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="create-unit">{t("inventory:filterByUnit")}</Label>
                              <Select
                                value={createUnit}
                                onValueChange={(v) => setCreateUnit(v as Unit)}
                              >
                                <SelectTrigger id="create-unit" className="mt-1 w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {INVENTORY_UNITS.map((u) => (
                                    <SelectItem key={u} value={u}>
                                      {t(`common:units.${u}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="create-kind">{t("inventory:kind")}</Label>
                              <Select
                                value={createKind}
                                onValueChange={(v) => {
                                  const kind = v as ProductKind;
                                  setCreateKind(kind);
                                  setCreateLicense(defaultLicenseKindForKind(kind));
                                }}
                              >
                                <SelectTrigger id="create-kind" className="mt-1 w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PRODUCT_KINDS.map((k) => (
                                    <SelectItem key={k} value={k}>
                                      {t(`inventory:productKind.${k}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="create-license">{t("inventory:licenseKind")}</Label>
                              <Select
                                value={createLicense}
                                onValueChange={(v) => setCreateLicense(v as LicenseKind)}
                              >
                                <SelectTrigger id="create-license" className="mt-1 w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {LICENSE_KINDS.map((l) => (
                                    <SelectItem key={l} value={l}>
                                      {t(`inventory:license.${l}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end gap-2 lg:col-span-4">
                              <Button
                                type="submit"
                                size="sm"
                                disabled={createMutation.isPending || createTitle.trim().length < 2}
                              >
                                {t("marketplaces:createAndLink")}
                              </Button>
                              {actionError && (
                                <p className="text-xs text-destructive">{actionError}</p>
                              )}
                            </div>
                          </form>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
            {(unmatchedQ.data?.length ?? 0) >= unmatchedLimit && (
              <div className="flex justify-center border-t p-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setUnmatchedLimit((n) => n + 50)}
                >
                  {t("marketplaces:loadMore")}
                </Button>
              </div>
            )}
          </Card>
        )}
      </section>

      <Dialog open={detailRowId !== null} onOpenChange={(open) => !open && setDetailRowId(null)}>
        <DialogContent>
          {(() => {
            const row = unmatchedQ.data?.find((r) => r.id === detailRowId);
            const detail = detailQ.data;
            const title = detail?.title ?? row?.title ?? row?.channelSku;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{title ?? t("marketplaces:unmatchedSkuDetail")}</DialogTitle>
                  {row && (
                    <DialogDescription>
                      {t(`marketplaces:name.${row.marketplace}`)} · {row.accountLabel} ·{" "}
                      <span className="font-mono">{row.channelSku}</span>
                    </DialogDescription>
                  )}
                </DialogHeader>

                {detailQ.isLoading && (
                  <p className="text-sm text-muted-foreground">{t("common:loading")}</p>
                )}
                {detailQ.isError && (
                  <p className="text-sm text-destructive">{t("marketplaces:detailFailed")}</p>
                )}

                {detail && (
                  <div className="space-y-4">
                    <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                      <div>
                        <dt className="text-xs text-muted-foreground">{t("marketplaces:price")}</dt>
                        <dd className="font-medium tabular-nums">
                          {money(detail.price, "USD", locale)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("marketplaces:published")}
                        </dt>
                        <dd className="font-medium tabular-nums">
                          {integer(detail.quantity, locale)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("marketplaces:status")}
                        </dt>
                        <dd className="font-medium">
                          {detail.status ? t(`marketplaces:listingStatus.${detail.status}`) : "—"}
                        </dd>
                      </div>
                      <div className="col-span-2 sm:col-span-3">
                        <dt className="text-xs text-muted-foreground">
                          {t("marketplaces:category")}
                        </dt>
                        <dd className="font-medium">{detail.category ?? "—"}</dd>
                      </div>
                    </dl>

                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">
                        {t("marketplaces:description")}
                      </p>
                      <p className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
                        {detail.description || t("marketplaces:noDescription")}
                      </p>
                    </div>

                    {detail.url && (
                      <a
                        href={detail.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary underline-offset-2 hover:underline"
                      >
                        {t("marketplaces:viewOnChannel")}
                      </a>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkCreateOpen} onOpenChange={(open) => !open && closeBulkCreate()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("marketplaces:bulkCreateTitle", { count: selectedUnmatchedIds.size })}
            </DialogTitle>
            <DialogDescription>{t("marketplaces:bulkCreateDescription")}</DialogDescription>
          </DialogHeader>

          {!bulkCreateResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <Label htmlFor="bulk-create-unit">{t("inventory:filterByUnit")}</Label>
                  <Select
                    value={bulkCreateUnit}
                    onValueChange={(v) => setBulkCreateUnit(v as Unit)}
                  >
                    <SelectTrigger id="bulk-create-unit" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVENTORY_UNITS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {t(`common:units.${u}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bulk-create-kind">{t("inventory:kind")}</Label>
                  <Select
                    value={bulkCreateKind}
                    onValueChange={(v) => {
                      const kind = v as ProductKind;
                      setBulkCreateKind(kind);
                      setBulkCreateLicense(defaultLicenseKindForKind(kind));
                    }}
                  >
                    <SelectTrigger id="bulk-create-kind" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(`inventory:productKind.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bulk-create-license">{t("inventory:licenseKind")}</Label>
                  <Select
                    value={bulkCreateLicense}
                    onValueChange={(v) => setBulkCreateLicense(v as LicenseKind)}
                  >
                    <SelectTrigger id="bulk-create-license" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSE_KINDS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {t(`inventory:license.${l}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("marketplaces:bulkCreateHint")}</p>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeBulkCreate}>
                  {t("marketplaces:cancel")}
                </Button>
                <Button
                  type="button"
                  disabled={bulkCreateMutation.isPending || selectedUnmatchedIds.size === 0}
                  onClick={() => bulkCreateMutation.mutate([...selectedUnmatchedIds])}
                >
                  {bulkCreateMutation.isPending
                    ? t("marketplaces:bulkCreateSubmitting")
                    : t("marketplaces:bulkCreateSubmit", { count: selectedUnmatchedIds.size })}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">
                {t("marketplaces:bulkCreateResultSummary", {
                  created: bulkCreateResult.created,
                  linked: bulkCreateResult.linkedToExisting,
                  siblings: bulkCreateResult.siblingsResolved,
                })}
              </p>
              {bulkCreateResult.skipped.length > 0 && (
                <div>
                  <p className="text-sm text-destructive">
                    {t("marketplaces:bulkCreateSkipped", {
                      count: bulkCreateResult.skipped.length,
                    })}
                  </p>
                  <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-xs text-muted-foreground">
                    {bulkCreateResult.skipped.map((s) => (
                      <li key={s.channelSku} className="font-mono">
                        {s.channelSku} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end">
                <Button type="button" onClick={closeBulkCreate}>
                  {t("marketplaces:bulkCreateDone")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {oversellRisk.length > 0 && (
        <section
          aria-labelledby="oversell-heading"
          className="mb-8 rounded-lg border border-[hsl(var(--chart-3)/0.45)] bg-[hsl(var(--chart-3)/0.10)] p-4"
        >
          <h2 id="oversell-heading" className="flex items-center gap-2 text-xl font-semibold">
            <ArrowLeftRight className="h-5 w-5" aria-hidden />
            {t("marketplaces:oversellRisk", { count: oversellRisk.length })}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketplaces:oversellExplainer")}
          </p>
          <Table className="mt-3 min-w-[34rem]">
            <TableHeader>
              <TableRow>
                <TableHead>{t("inventory:sku")}</TableHead>
                <TableHead>{t("marketplaces:channel")}</TableHead>
                <TableHead className="text-right">{t("marketplaces:published")}</TableHead>
                <TableHead className="text-right">{t("marketplaces:fbaFulfillable")}</TableHead>
                <TableHead className="text-right">{t("marketplaces:overBy")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {oversellRisk.map((d) => (
                <TableRow key={`${d.sku}-${d.marketplace}`}>
                  <TableCell className="font-mono text-xs">{d.sku}</TableCell>
                  <TableCell>{t(`marketplaces:name.${d.marketplace}`)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {integer(d.publishedQuantity, locale)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {integer(d.sellable, locale)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    +{integer(d.drift, locale)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      <section>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2>{t("marketplaces:listingMatrix")}</h2>
          <Input
            type="search"
            value={matrixSearch}
            onChange={(e) => setMatrixSearch(e.target.value)}
            placeholder={t("marketplaces:searchMatrix")}
            className="sm:max-w-xs"
          />
          <fieldset className="flex flex-wrap gap-1 border-0 p-0">
            <legend className="sr-only">{t("marketplaces:filterByChannel")}</legend>
            {(["all", ...availableMarketplaces] as const).map((m) => (
              <Button
                key={m}
                type="button"
                size="sm"
                variant={filter === m ? "default" : "ghost"}
                onClick={() => setFilter(m)}
              >
                {m === "all" ? t("marketplaces:allChannels") : t(`marketplaces:name.${m}`)}
              </Button>
            ))}
          </fieldset>
        </div>

        {listingsQ.isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

        {!listingsQ.isLoading && listings.length === 0 && (
          <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            {t("marketplaces:noListings")}
          </p>
        )}

        {listings.length > 0 && (
          <Card>
            {/* table-fixed — same fix as the Unmatched SKUs table above: without
                it, a long product title just grows the column under the
                browser's default `auto` layout instead of truncating, and
                overlaps whatever's next to it. */}
            <Table className="table-fixed min-w-[56rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">{t("inventory:sku")}</TableHead>
                  <TableHead className="w-32">{t("marketplaces:channel")}</TableHead>
                  <TableHead className="w-28">{t("marketplaces:status")}</TableHead>
                  <TableHead className="w-24 text-right">{t("marketplaces:price")}</TableHead>
                  <TableHead className="w-24 text-right">{t("marketplaces:published")}</TableHead>
                  <TableHead className="w-24 text-right">{t("inventory:sellable")}</TableHead>
                  <TableHead className="w-32">{t("marketplaces:externalId")}</TableHead>
                  <TableHead className="w-32">{t("marketplaces:lastSynced")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      <span className="block truncate font-mono text-xs">{l.sku}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {l.title}
                      </span>
                    </TableCell>
                    <TableCell>
                      {t(`marketplaces:name.${l.marketplace}`)}
                      <span className="block truncate text-xs text-muted-foreground">
                        {l.accountLabel}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[l.status]}>
                        {t(`marketplaces:listingStatus.${l.status}`)}
                      </Badge>
                      {l.suppressionReason && (
                        <span className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                          <Ban className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                          <span className="truncate">{l.suppressionReason}</span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(l.price, "USD", locale)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {integer(l.publishedQuantity, locale)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {integer(l.sellable, locale)}
                    </TableCell>
                    <TableCell className="truncate font-mono text-xs">
                      {l.externalId ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {whenever(l.lastSyncedAt, locale)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
        <p className="mt-4 text-sm text-muted-foreground">{t("marketplaces:manualSyncNote")}</p>
      </section>
    </>
  );
}

function Cell({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("text-lg font-semibold tabular-nums", bad && "text-destructive")}>
        {value}
      </dd>
    </div>
  );
}
